<template>
    <div v-if="languages.length > 1">
        <dropdown
            v-model="selected"
            :options="languages"
            :special-option="moreLanguagesOption"
            theme="inherit"
        />
    </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

import { SHOW_ALERT, SHOW_MODAL, SHOW_PROMPT } from '@/store/types/mutation-types';
import { getLanguages, ERROR } from '@/sitecore/sitecore-data-service';
import listenOnRootMixin from '@/mixins/listen-on-root';

export default {
    mixins: [listenOnRootMixin],

    props: {
        show: {
            type: Boolean,
            default: false,
        },

        screen: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            selected: '',
            lastSelected: '',
            languagesAll: [],

            moreLanguagesOption: { text: 'More languages', value: 'new' },
        };
    },

    computed: {
        ...mapState({
            changedData: state => state.page.changedData,
            pageData: state => state.page.pageData,
        }),

        languages() {
            // need to convert the languages to a <dropdown> friendly format
            return Object.entries(this.pageData.pageData.languages)
                .map(entry => ({ text: entry[1], value: entry[0] }));
        },
    },

    watch: {
        async selected(newLang, oldLang) {
            if (
                !oldLang // on initial load, do nothing
                || oldLang === 'new' // do nothing after cancel new lang
                || this.lastSelected === newLang // ignore on revert
            ) return;

            this.lastSelected = oldLang;

            if (newLang === 'new') {
                if (!this.languagesAll.length) {
                    this.getAllLanguages();
                }

                if (this.changedData) {
                    this.showPrompt({
                        template: 'unsaved-changes',
                        onContinue: () => this.showModal('language-modal'),
                        onCancel: () => this.selected = oldLang,
                    });
                } else {
                    this.showModal('language-modal');
                }
                return;
            }

            if (this.changedData) {
                this.showPrompt({
                    template: 'unsaved-changes',
                    onContinue: () => this.loadSelection(newLang),
                    onCancel: () => this.selected = oldLang,
                });
            } else {
                this.loadSelection(newLang);
            }
        },
    },

    created() {
        this.listenOnRoot('flex::language::update', this.onLanguageUpdate);
        this.selected = this.getCurrentLanguage();
    },

    methods: {
        ...mapMutations({
            showAlert: SHOW_ALERT,
            showModal: SHOW_MODAL,
            showPrompt: SHOW_PROMPT,
        }),

        getCurrentLanguage() {
            if (!this.pageData) {
                return this.$route.query.lang || 'en';
            }

            return this.pageData.pageData.language;
        },

        async getAllLanguages() {
            const response = await getLanguages();
            if (response.status === ERROR) {
                this.showAlert({ description: response.error });
                return;
            }

            this.languagesAll = response.data;
        },

        loadSelection(lang) {
            this.lastSelected = '';
            this.$root.$emit('flex::page::load', lang);
        },

        onLanguageUpdate(newLanguage) {
            this.selected = newLanguage;
        },
    },
};
</script>

<style lang="scss">
.language-modal {
    .modal-xl {
        width: 500px;
    }
}
</style>
